import React, { useCallback, useEffect, useState } from 'react';
import { roleLabel } from './roleLabel';

/**
 * FunctionSetAccess — who may use this station's restricted function sets
 * (function-set-permissions). A restricted set is hidden from everyone except the members of
 * its role, which the agent creates the first time this list loads. Lives under the station's
 * Access section, next to AccessSection.
 *
 * BFF: GET `/api/user-access/stations/{id}/function-sets`;
 *      POST/DELETE `…/function-sets/{set}/members[/{account_id}]`;
 *      account search: GET `/api/user-access/accounts?q=`.
 */
export default function FunctionSetAccess({ stationId }) {
  const [sets, setSets] = useState(null);
  const [error, setError] = useState(null);
  const base = `/api/user-access/stations/${stationId}/function-sets`;

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await fetch(base, { credentials: 'same-origin' });
      if (!r.ok) { setError(`HTTP ${r.status}: ${await r.text()}`); return; }
      setSets(((await r.json()).function_sets || []).filter((s) => s.restricted));
    } catch (e) { setError(String(e.message || e)); }
  }, [base]);
  useEffect(() => { if (stationId) load(); }, [stationId, load]);

  const call = async (url, method, body) => {
    const r = await fetch(url, {
      method, credentials: 'same-origin',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!r.ok) { setError(`${method} → HTTP ${r.status}: ${await r.text()}`); return false; }
    return true;
  };

  if (!stationId) return null;
  return (
    <div data-testid="function-set-access" style={{ marginTop: 18 }}>
      <h4 style={{ margin: '0 0 6px', fontSize: '0.9rem' }}>Restricted function sets</h4>
      {error && <div role="alert" style={{ color: '#c00', fontSize: '0.82rem' }}>{error}</div>}
      {!sets && !error && <div style={{ color: '#888' }}>Loading…</div>}
      {sets && sets.length === 0 && (
        <p style={{ fontSize: '0.82rem', color: '#555' }}>This station has no restricted function sets.</p>
      )}
      {sets && sets.map((s) => (
        <SetMembers key={s.name} set={s} base={base} call={call} onChanged={load} />
      ))}
    </div>
  );
}

function SetMembers({ set, base, call, onChanged }) {
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  const members = set.members || [];
  const memberIds = new Set(members.map((m) => m.account_id));
  const setUrl = `${base}/${encodeURIComponent(set.name)}/members`;

  const search = async (e) => {
    e.preventDefault();
    if (!q.trim()) { setFound([]); return; }
    const r = await fetch(`/api/user-access/accounts?q=${encodeURIComponent(q.trim())}`,
                          { credentials: 'same-origin' });
    setFound(r.ok ? ((await r.json()).accounts || []) : []);
  };
  const add = async (accountId) => {
    if (await call(setUrl, 'POST', { account_id: accountId })) { setFound([]); setQ(''); onChanged(); }
  };
  const remove = async (accountId) => {
    if (await call(`${setUrl}/${accountId}`, 'DELETE')) onChanged();
  };

  return (
    <div style={{ border: '1px solid #e3e3e3', borderRadius: 6, padding: '8px 10px', margin: '8px 0' }}>
      <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{(set.role && roleLabel(set.role.name)) || set.name}</div>
      <div style={{ fontSize: '0.78rem', color: '#666', margin: '2px 0 6px' }}>
        Only these accounts see and can use <code>{set.name}</code> on this station.
      </div>
      {members.length === 0 && <div style={{ fontSize: '0.82rem', color: '#888' }}>No members yet.</div>}
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {members.map((m) => (
          <li key={m.account_id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.84rem' }}>
            <span>{m.display_name || m.email || `account ${m.account_id}`}</span>
            <button type="button" onClick={() => remove(m.account_id)}
                    aria-label={`Remove ${m.display_name || m.account_id} from ${set.name}`}>Remove</button>
          </li>
        ))}
      </ul>
      <form onSubmit={search} style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a user by name or email"
               aria-label={`Find a user to add to ${set.name}`} style={{ flex: 1 }} />
        <button type="submit">Search</button>
      </form>
      {found.filter((a) => !memberIds.has(a.id)).map((a) => (
        <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.84rem', marginTop: 4 }}>
          <span>{a.display_name || a.email || `account ${a.id}`}{a.email ? ` · ${a.email}` : ''}</span>
          <button type="button" onClick={() => add(a.id)}
                  aria-label={`Add ${a.display_name || a.id} to ${set.name}`}>Add</button>
        </div>
      ))}
    </div>
  );
}
